/**
 * BugReportModal - Modal para reportar errores
 */
import { useState } from 'react';
import { X, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

export function BugReportModal({ isOpen, onClose }) {
  const { language } = useApp();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'visual',
    description: '',
    url: typeof window !== 'undefined' ? window.location.href : '',
  });

  const categories = {
    visual: language === 'es' ? 'Visual/UI' : 'Visual/UI',
    pokemon_data: language === 'es' ? 'Datos de Pokémon' : 'Pokémon Data',
    analytics: language === 'es' ? 'Analíticas' : 'Analytics',
    other: language === 'es' ? 'Otro' : 'Other',
  };

  const labels = {
    es: {
      title: 'Reportar Error',
      titleLabel: 'Título',
      categoryLabel: 'Categoría',
      descriptionLabel: 'Descripción',
      descriptionPlaceholder: 'Describe el error en detalle...',
      urlLabel: 'URL (automática)',
      submit: 'Enviar Reporte',
      cancel: 'Cancelar',
      submitted: 'Reporte Enviado',
      submittedMsg: 'Gracias por ayudarnos a mejorar.',
      error: 'Error al enviar',
      required: 'Este campo es requerido',
    },
    en: {
      title: 'Report a Bug',
      titleLabel: 'Title',
      categoryLabel: 'Category',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Describe the bug in detail...',
      urlLabel: 'URL (automatic)',
      submit: 'Submit Report',
      cancel: 'Cancel',
      submitted: 'Report Submitted',
      submittedMsg: 'Thank you for helping us improve.',
      error: 'Failed to submit',
      required: 'This field is required',
    },
  };

  const t = labels[language] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error(t.required);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/bug-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title.trim(),
          category: formData.category,
          description: formData.description.trim(),
          url: formData.url,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setSubmitted(true);
      toast.success(t.submittedMsg);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          title: '',
          category: 'visual',
          description: '',
          url: window.location.href,
        });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Bug report error:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            data-testid="bug-report-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50"
            data-testid="bug-report-modal"
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-semibold">{t.title}</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="bug-report-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-8 gap-3"
                  >
                    <CheckCircle className="w-12 h-12 text-green-500" />
                    <h3 className="text-lg font-semibold">{t.submitted}</h3>
                    <p className="text-sm text-muted-foreground text-center">{t.submittedMsg}</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium mb-2">{t.titleLabel}</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Ej: Imagen de Pokémon no carga"
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border',
                          'bg-background border-border text-foreground',
                          'placeholder-muted-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-violet-500'
                        )}
                        data-testid="bug-title-input"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium mb-2">{t.categoryLabel}</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border',
                          'bg-background border-border text-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-violet-500'
                        )}
                        data-testid="bug-category-select"
                      >
                        {Object.entries(categories).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium mb-2">{t.descriptionLabel}</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder={t.descriptionPlaceholder}
                        rows="4"
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border resize-none',
                          'bg-background border-border text-foreground',
                          'placeholder-muted-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-violet-500'
                        )}
                        data-testid="bug-description-textarea"
                      />
                    </div>

                    {/* URL (read-only) */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-muted-foreground">
                        {t.urlLabel}
                      </label>
                      <input
                        type="text"
                        value={formData.url}
                        readOnly
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border text-xs',
                          'bg-muted border-border text-muted-foreground',
                          'cursor-not-allowed'
                        )}
                        data-testid="bug-url-input"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        data-testid="bug-cancel-btn"
                      >
                        {t.cancel}
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-700 hover:to-rose-600 text-white"
                        data-testid="bug-submit-btn"
                      >
                        {loading ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin mr-2" />
                            {language === 'es' ? 'Enviando...' : 'Sending...'}
                          </>
                        ) : (
                          t.submit
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default BugReportModal;
